import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userPermissionsApi } from './userPermissions.api';
import { toast } from 'react-toastify';
import { USER_PERMISSIONS_KEYS } from './userPermissions.queryKeys';
import type { 
    UserAllowedCompanyEntry, 
    UserAllowedCategoryEntry,
    UserAllowedCompanyEntryResponse,
    UserAllowedCategoryEntryResponse
} from '@/types/userPermissions.types';
import type { ActionStatus } from '@/types/api.types';

// Allowed Companies Mutations
export const useAddAllowedCompanyMutation = (userId: string) => {
    const queryClient = useQueryClient();

    return useMutation<UserAllowedCompanyEntryResponse, Error, UserAllowedCompanyEntry>({
        mutationFn: (data) => userPermissionsApi.addAllowedCompany(userId, data),
        onSuccess: async (response) => {
            if (response.action_status) {
                toast.success('Η εταιρεία προστέθηκε επιτυχώς');
                await queryClient.invalidateQueries({ 
                    queryKey: USER_PERMISSIONS_KEYS.allowedCompanies(userId) 
                });
            } else {
                toast.error(response.action_message || 'Σφάλμα κατά την προσθήκη της εταιρείας');
            }
        },
        onError: (error) => {
            toast.error('Σφάλμα κατά την προσθήκη της εταιρείας');
            console.error('Add allowed company error:', error);
        }
    });
};

export const useDeleteAllowedCompanyMutation = (userId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ActionStatus, Error, string>({
        mutationFn: (allowedCompanyId) => userPermissionsApi.deleteAllowedCompany(userId, allowedCompanyId),
        onSuccess: async (response) => {
            if (response.action_status) {
                toast.success('Η εταιρεία αφαιρέθηκε επιτυχώς');
                await queryClient.invalidateQueries({ 
                    queryKey: USER_PERMISSIONS_KEYS.allowedCompanies(userId) 
                });
            } else {
                toast.error(response.action_message || 'Σφάλμα κατά την αφαίρεση της εταιρείας');
            }
        },
        onError: (error) => {
            toast.error('Σφάλμα κατά την αφαίρεση της εταιρείας');
            console.error('Delete allowed company error:', error);
        }
    });
};

// Allowed Categories Mutations
export const useAddAllowedCategoryMutation = (userId: string) => {
    const queryClient = useQueryClient();

    return useMutation<UserAllowedCategoryEntryResponse, Error, UserAllowedCategoryEntry>({
        mutationFn: (data) => userPermissionsApi.addAllowedCategory(userId, data),
        onSuccess: async (response) => {
            if (response.action_status) {
                toast.success('Η κατηγορία προστέθηκε επιτυχώς');
                await queryClient.invalidateQueries({ 
                    queryKey: USER_PERMISSIONS_KEYS.allowedCategories(userId) 
                });
            } else {
                toast.error(response.action_message || 'Σφάλμα κατά την προσθήκη της κατηγορίας');
            }
        },
        onError: (error) => {
            toast.error('Σφάλμα κατά την προσθήκη της κατηγορίας');
            console.error('Add allowed category error:', error);
        }
    });
};

export const useDeleteAllowedCategoryMutation = (userId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ActionStatus, Error, string>({
        mutationFn: (allowedCategoryId) => userPermissionsApi.deleteAllowedCategory(userId, allowedCategoryId),
        onSuccess: async (response) => {
            if (response.action_status) {
                toast.success('Η κατηγορία αφαιρέθηκε επιτυχώς');
                await queryClient.invalidateQueries({ 
                    queryKey: USER_PERMISSIONS_KEYS.allowedCategories(userId) 
                });
            } else {
                toast.error(response.action_message || 'Σφάλμα κατά την αφαίρεση της κατηγορίας');
            }
        },
        onError: (error) => {
            toast.error('Σφάλμα κατά την αφαίρεση της κατηγορίας');
            console.error('Delete allowed category error:', error);
        }
    });
};
